// Rate limiter to prevent excessive API calls
class RateLimiter {
  constructor() {
    this.requests = [];
    this.maxRequestsPerMinute = 50; // Conservative limit
    this.maxRequestsPerHour = 1000; // Stay well under monthly limit
    this.requestCounts = {
      daily: 0,
      monthly: 0
    };
    this.resetCounts();
  }

  // Check if request is allowed
  canMakeRequest() {
    const now = Date.now();
    
    // Clean old requests (older than 1 hour)
    this.requests = this.requests.filter(time => now - time < 60 * 60 * 1000);
    
    // Check per-minute limit
    const recentRequests = this.requests.filter(time => now - time < 60 * 1000);
    if (recentRequests.length >= this.maxRequestsPerMinute) {
      console.warn('Rate limit: Too many requests per minute');
      return false;
    }

    // Check per-hour limit
    if (this.requests.length >= this.maxRequestsPerHour) {
      console.warn('Rate limit: Too many requests per hour');
      return false;
    }

    return true;
  }

  // Record a request
  recordRequest() {
    const now = Date.now();
    this.requests.push(now);
    this.requestCounts.daily++;
    this.requestCounts.monthly++;
    
    // Log usage for monitoring
    if (this.requestCounts.daily % 10 === 0) {
      console.log(`API Usage: ${this.requestCounts.daily} requests today, ${this.requestCounts.monthly} this month`);
    }
  }

  // Get current usage stats
  getUsageStats() {
    const now = Date.now();
    const recentRequests = this.requests.filter(time => now - time < 60 * 60 * 1000);
    
    return {
      requestsLastHour: recentRequests.length,
      requestsToday: this.requestCounts.daily,
      requestsThisMonth: this.requestCounts.monthly,
      maxPerMinute: this.maxRequestsPerMinute,
      maxPerHour: this.maxRequestsPerHour
    };
  }

  // Reset daily/monthly counters (call this on app startup)
  resetCounts() {
    const stored = localStorage.getItem('api_usage_stats');
    if (stored) {
      try {
        const data = JSON.parse(stored);
        const today = new Date().toDateString();
        const currentMonth = new Date().getMonth();
        
        // Reset daily count if new day
        if (data.lastDate !== today) {
          this.requestCounts.daily = 0;
        } else {
          this.requestCounts.daily = data.daily || 0;
        }
        
        // Reset monthly count if new month
        if (data.lastMonth !== currentMonth) {
          this.requestCounts.monthly = 0;
        } else {
          this.requestCounts.monthly = data.monthly || 0;
        }
      } catch (e) {
        console.warn('Could not load API usage stats');
      }
    }
  }

  // Save usage stats to localStorage
  saveStats() {
    const stats = {
      daily: this.requestCounts.daily,
      monthly: this.requestCounts.monthly,
      lastDate: new Date().toDateString(),
      lastMonth: new Date().getMonth()
    };
    localStorage.setItem('api_usage_stats', JSON.stringify(stats));
  }

  // Check if approaching limits
  isApproachingLimits() {
    const stats = this.getUsageStats();
    return {
      hourlyWarning: stats.requestsLastHour > this.maxRequestsPerHour * 0.8,
      monthlyWarning: stats.requestsThisMonth > 3000, // 75% of estimated monthly limit
      shouldThrottle: stats.requestsThisMonth > 3500 // 90% of estimated monthly limit
    };
  }
}

const rateLimiter = new RateLimiter();
export default rateLimiter;